import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  IconButton,
  InputAdornment,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import type { ChangeEvent, FC, SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';

import ModalHeader from 'components/Modal/ModalHeader';
import CustomSwitchItem from 'components/MuiCustom/CustomSwitchItem';
import FilledTextField from 'components/MuiCustom/FilledTextField';

interface AddNetworkProps {
  ssid?: string;
  networkPassword?: string;
  setSsid: (value: string) => void;
  setNetworkPassword?: (value: string) => void;
  handleSubmitNetworkSetup: (event: SyntheticEvent<HTMLFormElement>) => void;
}

const SetupNetwork: FC<AddNetworkProps> = props => {
  const {
    ssid,
    networkPassword,
    setSsid,
    setNetworkPassword,
    handleSubmitNetworkSetup,
  } = props;

  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordInput, setShowPasswordInput] = useState<boolean>(!!ssid);

  useEffect(() => {
    if (!showPasswordInput) {
      setNetworkPassword?.('');
    }
  }, [setNetworkPassword, showPasswordInput]);

  const handleAddNetworkName = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    const trimmedSsid = value.trim();
    setSsid(trimmedSsid);
  };

  const handleAddNetworkPassword = (e: ChangeEvent<HTMLInputElement>): void => {
    setNetworkPassword?.(e.target.value);
  };

  return (
    <>
      <ModalHeader
        title={t('common:pages.settings.subpages.wifi.add_network')}
        description={t('common:pages.device_setup.network_setup.description')}
      />
      <Box height="100%" sx={{ overflow: 'hidden' }}>
        <form onSubmit={handleSubmitNetworkSetup}>
          <Box mb={4} mt={2}>
            <FilledTextField
              label={t('common:pages.settings.subpages.wifi.network_name')}
              name="ssid"
              type="text"
              value={ssid}
              autoComplete="off"
              onChange={handleAddNetworkName}
              fullWidth
            />
          </Box>
          <CustomSwitchItem mb={2} isSingleItem>
            <Typography align="center" variant="body1">
              {t('common:pages.settings.subpages.wifi.password')}
            </Typography>
            <Switch
              checked={showPasswordInput}
              onChange={() => setShowPasswordInput(!showPasswordInput)}
            />
          </CustomSwitchItem>
          {showPasswordInput ? (
            <Box mb={1}>
              <FilledTextField
                label={t('common:pages.settings.subpages.wifi.password')}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={networkPassword}
                autoComplete="off"
                onChange={handleAddNetworkPassword}
                fullWidth
                helperText={t(
                  'common:pages.device_setup.device_password_setup.password_validation',
                )}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          ) : null}
        </form>
      </Box>
    </>
  );
};

export default SetupNetwork;
